import { ComponentType } from 'react';
import styled, { useTheme, Interpolation } from 'styled-components';

import { BaseImage } from 'shared/components/BaseImage';

interface IImageProps {
  'data-qa'?: string;
  alt: string;
  className?: string;
  crop?: boolean;
  disableLazyLoad?: boolean;
  draggable?: string;
  fetchpriority?: 'high' | 'low' | 'auto';
  loader?: ComponentType;
  loading?: 'eager' | 'lazy';
  missingAssetPath?: string;
  noSrcModification?: boolean;
  onClick?: () => void;
  onError?: () => void;
  onLoad?: () => void;
  placeholderStyles?: Interpolation<Record<string, unknown>>[];
  resizeable?: boolean;
  sizes: string;
  src: string;
}

export const getImageHost = (host) => {
  const approvedImageOrigins = [
    'foodsworth.com',
    'goat.com',
    'goatchina.cn',
    'mosaic-images-staging.labs.goateng.com',
  ];

  if (host && approvedImageOrigins.some((origin) => host.includes(origin))) {
    return host;
  }

  return 'image.goat.com';
};

export const Image = ({ src, placeholderStyles, ...props }: IImageProps) => {
  // 🚨 ALERT
  // Sneakers is returning medium image urls in
  // productTemplate::productTemplateExternalPictures::mainPictureUrl
  // This is a patch until we can get a backend change that returns original pics
  // I include the slashes in our replace to assure we only replace full segments
  // just in case the word medium appears in the image title
  const theme = useTheme();
  const updatedSrc = src ? src?.replace('/medium/', '/original/') : 'missing.png';
  const missingAssetPath = props.missingAssetPath
    ? props.missingAssetPath
    : theme.navigation.missingAssetPath;

  const imageProps = {
    ...props,
    isMissingAsset: /missing.png/.test(updatedSrc) || !updatedSrc,
    loader: () => (
      <Image.PlaceholderLoader
        alt={props.alt}
        data-qa={props['data-qa']}
        src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
        missingAssetPath={missingAssetPath}
        placeholderStyles={placeholderStyles}
      />
    ),
    missingAssetPath,
    placeholderStyles,
    src: updatedSrc,
  };

  return <Image.StyledImage getImageHost={getImageHost} {...imageProps} />;
};

Image.StyledImage = styled(BaseImage)<{
  placeholderStyles: Interpolation<Record<string, unknown>>[];
  isMissingAsset: boolean;
  sizes?: string;
}>`
  width: 100%;
  max-width: 100%;
  max-height: ${({ sizes }) => sizes || '100%'};
  object-fit: contain;
  ${({ isMissingAsset, placeholderStyles }) => {
    return isMissingAsset ? placeholderStyles : '';
  }};
  background-origin: content-box !important;
`;

Image.PlaceholderLoader = styled.img<{
  missingAssetPath: string;
  placeholderStyles: Interpolation<Record<string, unknown>>[];
}>`
  background: url(${({ missingAssetPath }) => missingAssetPath}) no-repeat center;
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  ${({ placeholderStyles }) => placeholderStyles}
`;
